/**
 * Decodes IoTEmeter device warnings from the DEFINE `device_warnings` bitmask and the quick view
 * `hw_status`, following the "IOTEMETER Full permissions" sheet: a warning is reported only on the
 * device types the sheet marks Supported for it.
 *
 * Shared by the backend (DAO/shipmentDAO.js — Shipment Quality / Receive Shipment) and the frontend
 * (DeviceService — Site listing / Device Settings), so both decode the same way and cannot drift apart.
 * Keep this file free of require()/imports so the Angular client can import it as is.
 */

// sct_device_type values (Common/devices-config/deviceTypeConfig.js SCT_DEVICE_TYPES)
const SP1 = 100;
const SP3 = 101;
const IOTEMETER = 102;
const EDGE = 103;

const ALL_TYPES = [EDGE, SP1, SP3, IOTEMETER];
const NOT_SP1 = [EDGE, SP3, IOTEMETER]; // SP1 is single phase, so phase B/C rows are Not Supported on it
const EDGE_AND_IOTEMETER = [EDGE, IOTEMETER];
const ADE_TYPES = [SP1, SP3, IOTEMETER]; // devices with the internal ADE metering chip

// device_warnings bits are only reported by firmware 1.24 and later
const DEVICE_WARNINGS_MIN_FW_VERSION = 1.24;

// No warning of its own: when set, the voltage "Missing" bits read as "Exists"
const DEVICE_WARNINGS_MODIFIER_BIT = 0x8000;

const DEVICE_WARNING_BITS = [
	{ mask: 0x0001, warning: 'modbus_fault_warning', types: ALL_TYPES },
	{ mask: 0x0002, warning: 'ct_ratio_not_set_warning', types: EDGE_AND_IOTEMETER },
	{ mask: 0x0004, warning: 'wiring_mode_not_set_warning', types: EDGE_AND_IOTEMETER },
	{ mask: 0x0008, warning: 'voltage_a_missing_warning', modifiedWarning: 'voltage_a_exist_warning', types: ALL_TYPES },
	{ mask: 0x0010, warning: 'voltage_b_missing_warning', modifiedWarning: 'voltage_b_exist_warning', types: NOT_SP1 },
	{ mask: 0x0020, warning: 'voltage_c_missing_warning', modifiedWarning: 'voltage_c_exist_warning', types: NOT_SP1 },
	{ mask: 0x0040, warning: 'voltage_a_wrong_warning', types: ALL_TYPES },
	{ mask: 0x0080, warning: 'voltage_b_wrong_warning', types: NOT_SP1 },
	{ mask: 0x0100, warning: 'voltage_c_wrong_warning', types: NOT_SP1 },
	{ mask: 0x0200, warning: 'current_a_reversed_warning', types: ALL_TYPES },
	{ mask: 0x0400, warning: 'current_b_reversed_warning', types: NOT_SP1 },
	{ mask: 0x0800, warning: 'current_c_reversed_warning', types: NOT_SP1 },
	{ mask: 0x1000, warning: 'current_a_wrong_warning', types: ALL_TYPES },
	{ mask: 0x2000, warning: 'current_b_wrong_warning', types: NOT_SP1 },
	{ mask: 0x4000, warning: 'current_c_wrong_warning', types: NOT_SP1 },
];

// 0x04 (Ethernet Failure) is Not Supported on any device type, so it is never reported
const HW_STATUS_BITS = [
	{ mask: 0x01, warning: 'modbus', types: [EDGE] },
	{ mask: 0x01, warning: 'adc_error', types: ADE_TYPES },
	{ mask: 0x02, warning: 'rtc', types: ALL_TYPES },
	{ mask: 0x08, warning: 'flash_size', types: ALL_TYPES },
	{ mask: 0x10, warning: 'port_expander', types: ALL_TYPES },
	{ mask: 0x20, warning: 'cel_error', types: ALL_TYPES },
];

// imDeviceDAO.getDevicesInfo treats a missing or unknown sct_device_type as Edge; do the same here
const resolveDeviceType = (sctDeviceType) => {
	const deviceType = Number(sctDeviceType);
	return ALL_TYPES.includes(deviceType) ? deviceType : EDGE;
};

// An unknown (null) firmware version is decoded rather than silently skipped
const isDeviceWarningsSupported = (fwVersion) =>
	fwVersion === null || fwVersion === undefined || fwVersion >= DEVICE_WARNINGS_MIN_FW_VERSION;

/**
 * @param {object} device IM device row — reads sct_device_type, device_warnings, fw_version
 * @param {object} qv decompressed quick view — reads hw_status
 * @returns {string[]} warning keys (translated under `warnings.` in Common/translation/en.json)
 */
const decodeIMDeviceWarnings = (device, qv) => {
	const warnings = [];
	const deviceType = resolveDeviceType(device.sct_device_type);
	const deviceWarnings = device.device_warnings;

	if (deviceWarnings && isDeviceWarningsSupported(device.fw_version)) {
		const isModified = (deviceWarnings & DEVICE_WARNINGS_MODIFIER_BIT) !== 0;

		for (const { mask, warning, modifiedWarning, types } of DEVICE_WARNING_BITS) {
			if (types.includes(deviceType) && (deviceWarnings & mask) !== 0)
				warnings.push(isModified && modifiedWarning ? modifiedWarning : warning);
		}
	}

	const hwStatus = qv?.hw_status;
	if (hwStatus) {
		for (const { mask, warning, types } of HW_STATUS_BITS) {
			if (types.includes(deviceType) && (hwStatus & mask) !== 0)
				warnings.push(warning);
		}
	}

	return warnings;
};

module.exports = {
	decodeIMDeviceWarnings,
};
